/**
 * Synchro temps réel via le relay WebSocket NATS de l'api-central (`GET /ws`).
 *
 * Sur chaque CloudEvent tasks ou TAWA reçu, on invalide le cache TanStack Query :
 * les écrans re-fetchent et reflètent les mutations faites depuis n'importe
 * quel autre client (Workforce, autre onglet, autre utilisateur) sans reload.
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { User } from "oidc-client-ts";
import { env } from "./env";
import { getAccessToken, userManager } from "./auth/oidc";

/** subject NATS souscrit → préfixes de queryKey à invalider.
 * task-management publie `nesit.{tenant}.tasks.{aggregate}.{action}.v1` ;
 * TAWA publie `nesit.{tenant}.mo.tawa.workforce.{aggregate}.{action}.v1`
 * (events/subjects.go, BuildOutboundSubject). */
const SUBJECT_MAP: ReadonlyArray<{ subject: string; keys: string[][] }> = [
  {
    subject: `nesit.${env.VITE_TENANT_ID}.tasks.>`,
    keys: [["tasks"]],
  },
  {
    subject: `nesit.${env.VITE_TENANT_ID}.mo.tawa.>`,
    keys: [["tawa"]],
  },
];

function invalidateForSubject(qc: QueryClient, subject: string): void {
  for (const entry of SUBJECT_MAP) {
    // le préfixe hors wildcard suffit à router (`nesit.<tenant>.tasks.`)
    if (subject.startsWith(entry.subject.slice(0, -1))) {
      for (const key of entry.keys) {
        void qc.invalidateQueries({ queryKey: key });
      }
    }
  }
}

function invalidateAll(qc: QueryClient): void {
  for (const entry of SUBJECT_MAP) {
    for (const key of entry.keys) {
      void qc.invalidateQueries({ queryKey: key });
    }
  }
}

function buildWsUrl(token: string): string {
  const url = new URL("/ws", env.VITE_API_CENTRAL_BASE_URL);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  // les navigateurs n'autorisent pas de header custom au handshake WS
  url.searchParams.set("token", token);
  return url.toString();
}

/**
 * Durée pendant laquelle une connexion doit tenir avant d'être comptée comme
 * saine. Un `onopen` ne prouve rien : le relay accepte le handshake puis ferme
 * (token trop court, JWT invalide, NATS indisponible). Créditer l'ouverture
 * remettait le backoff à zéro à chaque tour, et le rattrapage relançait alors
 * TOUTES les queries `tawa` une fois par seconde, indéfiniment.
 */
const STABLE_AFTER_MS = 10_000;

function startRealtime(qc: QueryClient): () => void {
  let ws: WebSocket | null = null;
  let stopped = false;
  let attempt = 0;
  let everConnected = false;
  let retryTimer: number | undefined;
  let stableTimer: number | undefined;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = Math.min(30_000, 1000 * 2 ** attempt++);
    retryTimer = window.setTimeout(() => void connect(), delay);
  };

  const connect = async (): Promise<void> => {
    let token: string | null;
    try {
      token = await getAccessToken();
    } catch {
      // un throw (sessionStorage indispo, renew échoué) ne doit pas tuer la
      // boucle de retry : sans ce catch, `void connect()` avale le rejet.
      scheduleRetry();
      return;
    }
    if (stopped) return;
    if (!token) {
      scheduleRetry();
      return;
    }
    const socket = new WebSocket(buildWsUrl(token));
    ws = socket;
    socket.onopen = () => {
      socket.send(
        JSON.stringify({
          type: "subscribe",
          subjects: SUBJECT_MAP.map((e) => e.subject),
        }),
      );
      // Rattrape les events manqués — mais seulement au retour d'une connexion
      // qui avait tenu. Sans cette garde, un relay qui referme aussitôt fait
      // invalider en boucle des queries qui ne recevront jamais rien.
      if (everConnected) invalidateAll(qc);
      // Le crédit n'est accordé qu'à une connexion qui tient : c'est là, et
      // seulement là, qu'on relâche le backoff.
      stableTimer = window.setTimeout(() => {
        if (stopped || ws !== socket || socket.readyState !== WebSocket.OPEN)
          return;
        attempt = 0;
        everConnected = true;
      }, STABLE_AFTER_MS);
    };
    socket.onmessage = (e: MessageEvent) => {
      let msg: { type?: unknown; subject?: unknown };
      try {
        msg = JSON.parse(String(e.data)) as typeof msg;
      } catch {
        return;
      }
      if (msg.type === "event" && typeof msg.subject === "string") {
        invalidateForSubject(qc, msg.subject);
      }
    };
    socket.onclose = () => {
      window.clearTimeout(stableTimer);
      ws = null;
      scheduleRetry();
    };
  };

  // pousse le JWT renouvelé au relay avant expiry (sinon close 4401)
  const onUserLoaded = (user: User): void => {
    if (ws?.readyState === WebSocket.OPEN && user.access_token) {
      ws.send(JSON.stringify({ type: "refresh", token: user.access_token }));
    }
  };
  userManager.events.addUserLoaded(onUserLoaded);

  void connect();

  return () => {
    stopped = true;
    window.clearTimeout(retryTimer);
    window.clearTimeout(stableTimer);
    userManager.events.removeUserLoaded(onUserLoaded);
    ws?.close(1000);
    ws = null;
  };
}

/** À monter une fois, sous QueryClientProvider (cf. App.tsx). */
export function useRealtimeSync(): void {
  const qc = useQueryClient();
  useEffect(() => startRealtime(qc), [qc]);
}
